<script setup>
import { computed, inject, ref } from 'vue';
import { hasLink, styleVars, weekdayShort } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// An automatic list with nothing coming up says so: somebody checking
// for a ride or a work day learns there is none, which a missing section
// cannot tell them. A hand-picked list with nothing in it is an
// unfinished block, and a visitor gets none of it (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const source = computed(() => (props.config.source === 'auto' ? 'auto' : 'manual'));

// Exactly Classic's two sources: the events table, or the rows a
// manager typed into the editor. A manual row carries month / day
// rather than a calendar date, which is why nothing here derives one.
const items = computed(() => {
    if (source.value !== 'auto') return props.config.items || [];
    // 'all' is the Events page itself, which lists every occurrence.
    const all = props.site_data?.events || [];
    const count = props.config.upcoming_count === 'all' ? all.length : (Number(props.config.upcoming_count) || 6);
    return all.slice(0, count);
});

// Classic's tag colors. The design writes the tag as a quiet mono
// line rather than a colored pill, so the color rides a dot in front
// of it: the grouping survives without a block of five hues competing
// with the accent.
const TAG_COLORS = {
    party: '#9b59b6',
    volunteer: '#27ae60',
    ride: '#3498db',
    race: '#e74c3c',
    other: '#7f8c8d',
};
function tagColor(tag) {
    return TAG_COLORS[String(tag ?? '').toLowerCase()] || TAG_COLORS.other;
}

// ── Filters ─────────────────────────────────────────────────────────
const activeTag = ref('');
const tags = computed(() => {
    const seen = [];
    items.value.forEach((e) => {
        const t = String(e.tag ?? '').trim();
        if (t && !seen.includes(t)) seen.push(t);
    });
    return seen;
});
const showFilters = computed(() => !!props.config.show_filters && tags.value.length > 1);
const filtered = computed(() => {
    if (!showFilters.value || !activeTag.value) return items.value;
    return items.value.filter((e) => String(e.tag ?? '').trim() === activeTag.value);
});

const featured = computed(() => (props.config.layout === 'featured' ? (filtered.value[0] || null) : null));
const listItems = computed(() => (featured.value ? filtered.value.slice(1) : filtered.value));
const more = computed(() => props.config.more_link || {});

// "SAT OCT 25 · 9:00 AM". The weekday only exists for an auto row,
// which is the one that carries a calendar date.
function metaLine(ev) {
    const parts = [];
    if (ev.tag) parts.push(String(ev.tag));
    const day = [weekdayShort(ev.date), ev.month, ev.day].filter(Boolean).join(' ');
    if (day) parts.push(day);
    if (ev.time) parts.push(ev.time);
    return parts.join(' · ');
}
function whereLine(ev) {
    return [ev.time, ev.location, ev.audience].map((s) => String(s ?? '').trim()).filter(Boolean).join(' · ');
}
// A recurring event repeats its id once per occurrence, so the id
// alone is not a key.
function rowKey(ev, i) {
    return `${ev.id ?? 'row'}-${ev.date ?? ''}-${i}`;
}
</script>

<template>
    <section v-if="items.length || source === 'auto' || editing" :id="config.anchor_id || null" class="th-block th-sec th-events" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.subtitle || hasLink(more)" class="th-head th-events__head">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                    <p v-if="config.subtitle" class="th-lede th-events__sub">{{ config.subtitle }}</p>
                </div>
                <a v-if="hasLink(more)" :href="more.href || '/events'" class="th-more">{{ more.label }} →</a>
            </div>

            <div v-if="showFilters" class="th-chips th-events__chips">
                <button type="button" class="th-chip th-events__chip" :class="{ 'is-active': activeTag === '' }" @click="activeTag = ''">All</button>
                <button v-for="tag in tags" :key="tag"
                        type="button"
                        class="th-chip th-events__chip"
                        :class="{ 'is-active': activeTag === tag }"
                        @click="activeTag = tag">{{ tag }}</button>
            </div>

            <div v-if="featured" class="th-events__featured th-dark">
                <div class="th-events__featured-body">
                    <p v-if="metaLine(featured)" class="th-events__featured-meta">
                        <span class="th-events__tag-dot" :style="{ backgroundColor: tagColor(featured.tag) }"></span>{{ metaLine(featured) }}
                    </p>
                    <h3 class="th-h2 th-events__featured-title">{{ featured.name }}</h3>
                    <p v-if="featured.location" class="th-body th-events__featured-where">{{ featured.location }}</p>
                    <div class="th-row th-events__featured-row">
                        <a v-if="featured.href" :href="featured.href" class="th-btn th-btn--primary">Event details</a>
                        <span v-if="featured.audience" class="th-events__spots">{{ featured.audience }}</span>
                    </div>
                </div>
                <div class="th-events__featured-tile">
                    <span class="th-events__featured-m">{{ featured.month }}</span>
                    <span class="th-events__featured-d">{{ featured.day }}</span>
                </div>
            </div>

            <div v-if="listItems.length" class="th-events__list">
                <div v-for="(ev, i) in listItems" :key="rowKey(ev, i)" class="th-events__row">
                    <div class="th-date">
                        <span class="th-date__m">{{ ev.month }}</span>
                        <span class="th-date__d">{{ ev.day }}</span>
                    </div>
                    <div class="th-events__row-main">
                        <p v-if="metaLine(ev)" class="th-events__row-meta">
                            <span class="th-events__tag-dot" :style="{ backgroundColor: tagColor(ev.tag) }"></span>{{ metaLine(ev) }}
                        </p>
                        <h3 class="th-h3 th-events__row-title">{{ ev.name }}</h3>
                        <div v-if="whereLine(ev)" class="th-events__row-where">{{ whereLine(ev) }}</div>
                    </div>
                    <a v-if="ev.href" :href="ev.href" class="th-btn th-btn--ghost th-btn--sm th-events__row-cta">Details</a>
                </div>
            </div>

            <p v-if="filtered.length === 0 && (source === 'auto' || items.length || editing)" class="th-small th-events__empty">
                {{ items.length ? 'No events with that tag.'
                    : (source === 'auto' ? 'No upcoming events right now.' : 'Add events in the editor.') }}
            </p>
        </div>
    </section>
</template>

<style scoped>
.th-events__sub { margin-top: 12px; }
.th-events__head { margin-bottom: 20px; }
.th-events__chips { margin-bottom: 20px; }
.th-events__chip { text-transform: capitalize; }

.th-events__featured {
    border-radius: var(--r);
    overflow: hidden;
    display: flex;
    flex-wrap: wrap-reverse;
    align-items: stretch;
    gap: 0;
    margin-bottom: clamp(24px, 4vw, 40px);
}
.th-events__featured-body {
    flex: 1 1 320px;
    padding: clamp(24px, 4vw, 48px);
    display: flex;
    flex-direction: column;
    gap: 14px;
    justify-content: center;
}
.th-events__featured-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font: 500 12px/1 var(--th-mono);
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--text-eyebrow, #c8ccc5);
}
.th-events__featured-title { margin: 0; }
.th-events__featured-where { max-width: 44ch; }
.th-events__featured-row { margin-top: 6px; }
.th-events__spots {
    font: 400 12px/1 var(--th-mono);
    letter-spacing: .06em;
    text-transform: uppercase;
    color: #c8ccc5;
}
/* The events feed carries no photograph, so the design's photo column
   is the calendar tile at band scale rather than a hatched
   placeholder nobody can fill. */
.th-events__featured-tile {
    flex: 1 1 200px;
    min-height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: rgba(255, 255, 255, .06);
    border-left: 1px solid rgba(255, 255, 255, .12);
}
.th-events__featured-m { font: 500 13px/1 var(--th-mono); letter-spacing: .16em; color: var(--accent); }
.th-events__featured-d { font: 800 clamp(56px, 8vw, 92px)/1 var(--th-display); }

.th-events__list { display: flex; flex-direction: column; border-top: 1px solid var(--th-rule); }
.th-events__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 24px;
    padding: 20px 0;
    border-bottom: 1px solid var(--th-rule);
}
.th-events__row-main { flex: 1; min-width: 200px; }
.th-events__row-meta {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 6px;
    font: 500 11px/1 var(--th-mono);
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--th-meta);
}
.th-events__tag-dot { width: 7px; height: 7px; border-radius: 50%; flex: none; display: inline-block; }
.th-events__row-title { font-size: 20px; margin-bottom: 4px; }
.th-events__row-where { font-size: 15px; color: var(--text-sub, var(--th-muted)); }
.th-events__row-cta { flex: none; }
.th-events__empty { font-style: italic; padding-top: 16px; }

@media (max-width: 560px) {
    .th-events__featured-tile { min-height: 120px; }
}
</style>
